class Carousel extends DrupalBehavior {
  onContent(context) {
    console.log('Carousel Code!');
    context.querySelectorAll('.carousel').forEach((carousel) => {
      this.init(carousel);
    });
  }

  init(carousel) {
    const items = carousel.querySelector('.carousel--items');
    if (!items) {
      return;
    }

    const slides = items.querySelectorAll('.carousel--items > *');
    slides.forEach((slide) => {
      slide.classList.add('keen-slider__slide');
    });
    slides[0].classList.add('active-slide');

    items.classList.add('keen-slider');

    const slidesPerView = parseInt(carousel.dataset.slidesPerView, 10) || 3;
    // Read once and shared with the option below, so the track arithmetic in
    // `sizeTrack` cannot drift from the spacing keen is actually given.
    const spacing = 24;
    const loop = carousel.dataset.loop === 'true';
    // The width below which the slider drops to one slide per view. 921 is what
    // every carousel has used until now and stays the default, because
    // carousel.scss switches the matching `.carousel` chrome at 922: the two are
    // a pair, and moving one on its own would split them. A carousel can name
    // its own instead, which the campaign ones do so they drop to one card at
    // the theme's own 992 desktop breakpoint (sm-max), where the rest of their
    // CSS already switches.
    const mobileBreakpoint = parseInt(carousel.dataset.mobileBreakpoint, 10) || 921;
    const prev = carousel.querySelector('.carousel--prev');
    const next = carousel.querySelector('.carousel--next');
    // Opt-in pagination dots (SUM-1308). A carousel gets them only by putting an
    // empty `.carousel--dots` element in its markup, so every existing carousel
    // — which has none — behaves exactly as before. The pattern is the one
    // Testimonials.js already uses: `.dot` / `.dot--active` buttons built from a
    // wrapper in `created`, lifted here so the two stay consistent. Where this
    // differs is the sync hook — see `slideChanged` below.
    const dots = carousel.querySelector('.carousel--dots');

    // Cards are always the width the design draws and never stretch to fill the
    // track when a carousel holds fewer of them than it shows (SUM-1319).
    //
    // keen-slider clamps `slidesPerView` down to the slide count — `At()` in
    // keen-slider.esm.js is `clamp(slidesPerView, 1, max(size, 1))` — and then
    // sizes each slide as a fraction of the track. Two locations in a three-up
    // carousel are therefore sized `calc(50% - 12px)` and come out 708px where
    // the frame draws 464. Handing `slidesPerView` a function skips that clamp,
    // because `At()` returns a function's result untouched, but it leaves the
    // rest of keen's arithmetic reading a three-up track that does not exist:
    // `Yt()` clamps a slide index to `size - 1 - (perView - 1)`, which is
    // negative at two slides, and the track initialises off-position.
    //
    // So the track is narrowed and keen's own clamped arithmetic is left to land
    // on the designed width by itself. For `o` slides in a `p`-up carousel with
    // `g` of spacing, a full track `W` wide draws each card at
    // `(W - (p - 1)g) / p`, so the track that holds `o` of them at that width is
    // `o * card + (o - 1)g`. keen divides that by its clamped perView, which is
    // `o`, and arrives back at the card width the frame draws.
    //
    // `W` is measured rather than written as a percentage of the track's own
    // 100%. Between sm-max and md-max the chevrons come inside and join the
    // track in one flex row, so the track's percentage basis is the whole row
    // including them and only its natural flex width is the space the cards
    // actually get. Measuring covers that band and the plain desktop one with
    // the same arithmetic.
    //
    // Opt-in through `data-fixed-slide-width`, the pattern `.carousel--dots`
    // set in SUM-1308: every existing carousel keeps stretching as it does
    // today, so this cannot reach testimonials, awards, featured cards or the
    // team-group cards without their template asking for it.
    const fixedSlideWidth = carousel.hasAttribute('data-fixed-slide-width');

    const sizeTrack = () => {
      // `flex-grow` as well as the width: the track is a flex item in the band
      // where the chevrons sit beside it, and a width on its own would be a
      // basis it grows straight back out of.
      items.style.removeProperty('width');
      items.style.removeProperty('flex-grow');
      // Below the mobile breakpoint keen shows one slide, so there is no
      // shortfall to correct and the track keeps its full width.
      const perView = window.innerWidth <= mobileBreakpoint ? 1 : slidesPerView;
      if (slides.length >= perView) {
        return;
      }
      // Read with the override cleared, so this is the width a full complement
      // of cards would have had. Zero means the carousel is not being rendered
      // — inside something collapsed, or detached — and there is nothing to
      // measure against yet.
      const full = items.offsetWidth;
      if (!full) {
        return;
      }
      const card = (full - (perView - 1) * spacing) / perView;
      items.style.flexGrow = '0';
      items.style.width = `${slides.length * card + (slides.length - 1) * spacing}px`;
    };

    const updateButtons = (instance) => {
      const { relativeSlide, size, slidesPerView } = instance.details();
      if (prev) prev.style.visibility = relativeSlide === 0 ? 'hidden' : '';
      if (next) next.style.visibility = relativeSlide >= size - slidesPerView ? 'hidden' : '';
    };

    const buildDots = (instance) => {
      // onContent runs for page fragments as well as page load, so start from
      // empty rather than appending a second set.
      dots.textContent = '';
      slides.forEach((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.classList.add('dot');
        // "Slide 2 of 3" rather than a bare number: three buttons labelled only
        // by position are indistinguishable in a screen reader's element list.
        dot.setAttribute('aria-label', Drupal.t('Slide @number of @total', {
          '@number': index + 1,
          '@total': slides.length,
        }));
        dot.addEventListener('click', () => {
          instance.moveToSlide(index);
        });
        dots.appendChild(dot);
      });
    };

    const updateDots = (instance) => {
      const { relativeSlide } = instance.details();
      dots.querySelectorAll('.dot').forEach((dot, index) => {
        const isActive = index === relativeSlide;
        dot.classList.toggle('dot--active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    };

    // Before keen measures the track, so the first paint is already right.
    if (fixedSlideWidth) sizeTrack();

    const slider = new KeenSlider(items, {
      loop: loop,
      slidesPerView: slidesPerView,
      spacing: spacing,
      breakpoints: {
        [`(max-width: ${mobileBreakpoint}px)`]: {
          slidesPerView: 1,
        },
      },
      created: (instance) => {
        if (prev) {
          prev.addEventListener('click', () => {
            instance.prev();
          });
        }
        if (next) {
          next.addEventListener('click', () => {
            instance.next();
          });
        }
        if (!loop) updateButtons(instance);
        if (dots) {
          buildDots(instance);
          updateDots(instance);
        }
      },
      afterChange: (instance) => {
        const previousSlide = items.querySelector('.active-slide');
        if (previousSlide) {
          previousSlide.classList.remove('active-slide');
        }
        slides[instance.details().relativeSlide].classList.add('active-slide');
        if (!loop) updateButtons(instance);
        if (dots) updateDots(instance);
      },
      // `afterChange` is not enough to keep the controls in step. keen-slider
      // 5.2.2 only dispatches it when an animation ends with no completion
      // callback of its own, so a drag reaches it but a programmatic move — an
      // arrow click, or a dot click — does not: the "prev" arrow hidden on the
      // first slide would stay hidden for the rest of the carousel.
      // `slideChanged` fires whenever the index itself changes, which is what
      // both controls actually want.
      //
      // Gated on the dots element, which is what marks a carousel as opting in
      // to this: the existing ones have none, so they keep exactly the
      // behaviour they have today, quirk included, and this runs for them as a
      // single early return.
      slideChanged: (instance) => {
        if (!dots) {
          return;
        }
        if (!loop) updateButtons(instance);
        updateDots(instance);
      },
    });

    // keen registers its own resize listener while it mounts, so it re-measures
    // the track before this runs and reads the width the previous breakpoint
    // left behind. Re-sizing the track and asking keen to measure again puts it
    // right, which matters at the mobile breakpoint in both directions: the
    // narrowed track has to go back to full width as the carousel drops to one
    // slide, and come back as it returns to three.
    if (fixedSlideWidth) {
      let pending = null;
      window.addEventListener('resize', () => {
        if (pending) return;
        pending = window.requestAnimationFrame(() => {
          pending = null;
          sizeTrack();
          slider.resize();
        });
      });
    }
  }
}

DrupalBehavior.register('summit_theme', 'carousel', Carousel);
